import React, { useEffect, useMemo, useState } from "react";
import CommonModal from "./CommonModal";
import { callAPI } from "../../utils/apiUtils";
import { apiUrls } from "../../utils/apiUrls";
import { toast } from "react-toastify";

const UNZONED_KEY = "unzoned";

/**
 * Tables here come from Table::getAllByRestaurant() (via RESERVATION_TABLE_LIST), which includes
 * the special non-seating "tables" backing the Person/Tabs/Takeaway POS tabs - those have no
 * real shape/seat count and aren't something a reservation gets seated at, so they're filtered
 * out here rather than shown as assignable.
 */
const isRealTable = (table) => !!table.shape;

const AssignTableModal = ({
  isOpen,
  onClose,
  onBack,
  bookingId,
  guests,
  onAssigned,
}) => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [activeZone, setActiveZone] = useState(null);
  const [loading, setLoading] = useState(false);

  // The smallest seat count that can still fit the whole party - every table with exactly that
  // capacity gets flagged "Recommended" (covers ties, e.g. three 4-seaters for a party of 3).
  // null when the guest count is unknown or no table is big enough, so nothing gets highlighted
  // rather than suggesting an undersized table.
  const recommendedCapacity = useMemo(() => {
    const guestCount = Number(guests);
    if (!guestCount || tables.length === 0) return null;

    const fittingCapacities = tables
      .map((table) => Number(table.number_of_people))
      .filter((capacity) => capacity >= guestCount);

    return fittingCapacities.length > 0 ? Math.min(...fittingCapacities) : null;
  }, [tables, guests]);

  const isRecommended = (table) =>
    recommendedCapacity !== null && Number(table.number_of_people) === recommendedCapacity;

  const fetchTables = async () => {
    try {
      const resp = await callAPI(apiUrls.RESERVATION_TABLE_LIST, {}, "GET");
      const list = Array.isArray(resp?.data?.tables)
        ? resp.data.tables
        : Array.isArray(resp?.data)
        ? resp.data
        : [];
      setTables(list.filter(isRealTable));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load table list");
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedTable(null);
      fetchTables();
    }
  }, [isOpen]);

  // Group tables by zone - only zones that actually have a table in them show up as a tab, and
  // any table without a zone (not yet placed in the floor-plan editor) falls into its own
  // "Other Tables" tab rather than being silently dropped.
  const zoneGroups = useMemo(() => {
    const groups = new Map();
    tables.forEach((table) => {
      const key = table.zone_id ?? UNZONED_KEY;
      const label = table.zone_id ? (table.zone_name || `Zone ${table.zone_id}`) : "Other Tables";
      if (!groups.has(key)) {
        groups.set(key, { key, label, tables: [] });
      }
      groups.get(key).tables.push(table);
    });
    return Array.from(groups.values());
  }, [tables]);

  useEffect(() => {
    if (zoneGroups.length === 0) {
      setActiveZone(null);
      return;
    }
    if (!zoneGroups.some((g) => g.key === activeZone)) {
      setActiveZone(zoneGroups[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneGroups]);

  const activeTables = useMemo(() => {
    const list = zoneGroups.find((g) => g.key === activeZone)?.tables || [];
    if (recommendedCapacity === null) return list;

    // Recommended tables float to the top of their zone's list - order is otherwise unchanged.
    return [...list].sort((a, b) => Number(isRecommended(b)) - Number(isRecommended(a)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneGroups, activeZone, recommendedCapacity]);

  const handleAssign = async () => {
    if (!selectedTable) {
      toast.error("Please select a table");
      return;
    }

    setLoading(true);
    try {
      const payload = { id: bookingId, table_id: selectedTable };
      const resp = await callAPI(apiUrls.RESERVATION_ASSIGN_TABLE, {}, "POST", payload, {});
      if (resp.data?.status === 1) {
        toast.success(resp.data?.message || "Table assigned successfully");
        onAssigned(); // refresh  kar do calendar
        onClose();
      } else {
        // resp.data.errors carries Reservation::getErrors() when the save() failed validation -
        // surfacing it (instead of just the generic message) is what actually shows why, since
        // this endpoint's only failure modes are "not found" or a validation error.
        console.error('Assign table failed', resp.data);
        toast.error(resp.data?.message || "Failed to assign table");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error assigning table");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="assignTableModal">
      <div className="modal-header">
        <button type="button" className="btn-close" onClick={onClose}></button>
      </div>

      <div className="modal-body">
        <div className="modalBodyStyle">
          {zoneGroups.length > 0 ? (
            <>
              <div className="hardwareTabBar mb-3" role="tablist">
                {zoneGroups.map((group) => (
                  <button
                    key={group.key}
                    type="button"
                    className={`hardwareTabBtn ${activeZone === group.key ? 'active' : ''}`}
                    role="tab"
                    aria-selected={activeZone === group.key}
                    onClick={() => setActiveZone(group.key)}
                  >
                    {group.label}
                  </button>
                ))}
              </div>

              <div className="noOfChairModalInner">
                {activeTables.map((table) => {
                  const recommended = isRecommended(table);
                  const selected = selectedTable === table.id;
                  return (
                    <div
                      className={`form-check mb-2 tableOptionRow${recommended ? ' tableOptionRecommended' : ''}${selected ? ' tableOptionSelected' : ''}`}
                      key={table.id}
                    >
                      <input
                        className="form-check-input"
                        type="radio"
                        name="selectedTable"
                        id={`table_${table.id}`}
                        checked={selectedTable === table.id}
                        onChange={() => setSelectedTable(table.id)}
                      />
                      <label className="form-check-label" htmlFor={`table_${table.id}`}>
                        {table.name || `Table-${table.id}`}
                        {table.number_of_people ? (
                          <span className="tableSeatCount"> ({table.number_of_people} seats)</span>
                        ) : null}
                        {recommended && <span className="tableRecommendedBadge">Recommended</span>}
                      </label>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center py-3 text-muted">No tables available</div>
          )}
        </div>
      </div>

      <div className="modal-footer justify-content-between">
        <button type="button" className="btn btnLight" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="btn btnPrimary"
          onClick={handleAssign}
          disabled={loading}
        >
          {loading ? "Assigning..." : "Assign"}
        </button>
      </div>
    </CommonModal>
  );
};

export default AssignTableModal;
